import { useState, type FC, useRef } from 'react'

import { Tooltip } from '@skytech/ui'
import { useNavigate } from 'react-router-dom'
import { twJoin } from 'tailwind-merge'

import { useOnClickOutside } from '@/hooks/useOnClickOutside'
import { Amount } from '@/modules/admin/drilldown/org-chart/Amount'
import { APP_ROUTES } from '@/util/app-routes'

import { setMultipleDrillDownAction } from '../context/actions.context'
import { useDrillDownContext } from '../context/index.context'
import type { CustomerData } from '../types'

import { LinkComponent } from './Link'
import { Menu } from './Menu'

import type { DrillDownType } from '../../resources'

export const Customer: FC<{ customer: CustomerData }> = ({ customer }) => {
  const { state, dispatch } = useDrillDownContext()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const menuRef = useRef<HTMLButtonElement>(null)

  useOnClickOutside(menuRef, () => setIsMenuOpen(false))

  const navigate = useNavigate()

  const onClick = (pathname: string) => {
    const params = {
      type: 'customer' as DrillDownType,
      typeId: String(customer.id),
      period: String(state.period),
      year: String(state.year),
      currency: state.currency,
    }

    const search = new URLSearchParams(params).toString()

    dispatch(setMultipleDrillDownAction({ type: 'customer', typeId: String(customer.id) }))

    navigate({ pathname, search })
  }

  const amounts = Array.from(customer.values.entries()).map(([currency, value]) => ({
    currency,
    value,
  }))

  return (
    <button
      ref={menuRef}
      className={twJoin(
        'm-2 max-h-[5.2rem] rounded-[4px] border-none bg-gray-100 p-0 py-2 transition-all duration-[250ms] ease-out hover:scale-[106%]',
        isMenuOpen && 'scale-[106%]'
      )}
      onClick={() => setIsMenuOpen(!isMenuOpen)}
    >
      <div className="mb-4 flex h-min min-h-full w-full max-w-full cursor-pointer flex-col rounded-[4px] border-none px-2">
        <div className="max-w-full">
          <span
            data-tooltip-id={String(customer.id)}
            data-tooltip-content={customer.name}
            className="mb-[0.2em] block overflow-hidden text-ellipsis whitespace-nowrap text-[1rem] font-normal text-[#333333]"
            data-event="click focus"
          >
            <Tooltip id={String(customer.id)} style={{ zIndex: 900 }} className="break-all" />
            {customer.name}
          </span>
          <span className="mt-[-0.2em] block text-[0.7rem] text-[#0000008D]">{customer.orgNo}</span>
          <div className="mt-[0.2em] text-[1.2rem]">
            {amounts.map(({ value, currency }) => (
              <Amount
                key={currency}
                amount={value}
                currency={currency}
                rotateColors
                onClick={() => {}}
              />
            ))}
          </div>
        </div>
      </div>

      <Menu className="mt-0 bg-gray-100" selected={isMenuOpen}>
        <LinkComponent
          className="bg-white hover:bg-gray-50"
          onClick={() => onClick(`${APP_ROUTES.REPORTS_DRILL_DOWN_SERVICE}`)}
          label="Services"
        />
        <LinkComponent
          className="bg-white hover:bg-gray-50"
          onClick={() => onClick(`${APP_ROUTES.REPORTS_DRILL_DOWN_DEPARTMENT}`)}
          label="Dept. / Cost Center"
        />
        <LinkComponent
          className="bg-white hover:bg-gray-50"
          onClick={() => onClick(`${APP_ROUTES.REPORTS_DRILL_DOWN_COST_OBJECT}`)}
          label="Number / Cost Item"
        />
      </Menu>
    </button>
  )
}
